/**
 * @module game/PowerUp
 * Power-up entity — slashing a power-up activates a timed gameplay modifier.
 *
 * Available power-ups:
 *  - freeze  — slows all fruit/bomb velocity to CONFIG.powerup.freezeVelocityScale
 *              for CONFIG.powerup.freezeDuration ms.
 *  - double  — doubles all score for CONFIG.powerup.doublePointsDuration ms.
 *  - magnet  — attracts nearby fruits toward the pointer for CONFIG.powerup.magnetDuration ms.
 *
 * Functional API per module contract.
 * Legacy `PowerUpEntity` interface kept for any pre-existing internal references.
 */

import type { PowerUp, Vec2, Fruit } from '../types.js';
import type { PowerUpType } from '../game-config.js';
import { CONFIG } from '../game-config.js';
import { stepBody, isOffscreen } from './physics.js';

export type { PowerUpType };

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let _nextPowerUpId = 0;

// Active effect timers — remaining duration (ms), or null = inactive.
interface ActiveEffects {
  freeze: number | null;
  double: number | null;
  magnet: number | null;
}

const _active: ActiveEffects = {
  freeze: null,
  double: null,
  magnet: null,
};

// ---------------------------------------------------------------------------
// Minimal PixiJS-compatible interfaces
// ---------------------------------------------------------------------------

interface SpriteRef {
  x: number;
  y: number;
  visible: boolean;
  destroy?(): void;
}

interface ContainerRef {
  addChild(child: SpriteRef): void;
  removeChild(child: SpriteRef): void;
}

/** PowerUp with internal render state. */
interface PowerUpInternal extends PowerUp {
  _sprite: SpriteRef | null;
}

// ---------------------------------------------------------------------------
// Public functional API
// ---------------------------------------------------------------------------

/**
 * Create a PowerUp entity and add its sprite to the given container.
 *
 * @param type      - Which power-up kind to spawn.
 * @param position  - Initial canvas position (px).
 * @param velocity  - Initial velocity vector (px/s).
 * @param container - PixiJS Container to add the sprite to.
 */
export function createPowerUp(
  type: PowerUpType,
  position: Vec2,
  velocity: Vec2,
  container: ContainerRef,
): PowerUp {
  const sprite: SpriteRef = {
    x: position.x,
    y: position.y,
    visible: true,
  };

  container.addChild(sprite);

  const pu: PowerUpInternal = {
    id: _nextPowerUpId++,
    type,
    x: position.x,
    y: position.y,
    vx: velocity.x,
    vy: velocity.y,
    hitRadius: CONFIG.powerup.hitRadius,
    sprite,
    _sprite: sprite,
  };

  return pu;
}

/**
 * Update power-up physics for one render frame.
 *
 * @param powerUp     - The power-up entity to update.
 * @param dt          - Frame delta in seconds, scaled by current timeScale.
 * @param _canvasWidth - Canvas width (px) — unused but part of contract signature.
 * @param canvasHeight - Canvas height (px) — used to check offscreen.
 * @returns true if the power-up has exited the screen.
 */
export function updatePowerUp(
  powerUp: PowerUp,
  dt: number,
  _canvasWidth: number,
  canvasHeight: number,
): boolean {
  const pu = powerUp as PowerUpInternal;

  stepBody(pu, dt, CONFIG.fruit.gravity);

  if (pu._sprite !== null) {
    pu._sprite.x = pu.x;
    pu._sprite.y = pu.y;
  }

  return isOffscreen(pu, canvasHeight);
}

/**
 * Activate the power-up effect.
 *
 * Freeze: applies freezeVelocityScale velocity multiplier to all active
 *   fruits/obstacles for CONFIG.powerup.freezeDuration ms (handled in tickActivePowerUps).
 * Double: sets doublePoints flag for CONFIG.powerup.doublePointsDuration ms.
 * Magnet: sets magnetActive flag for CONFIG.powerup.magnetDuration ms.
 *
 * If the same type is already active, resets its timer.
 *
 * @param type - Which power-up to activate.
 */
export function activatePowerUp(type: PowerUpType): void {
  switch (type) {
    case 'freeze':
      _active.freeze = CONFIG.powerup.freezeDuration;
      break;
    case 'double':
      _active.double = CONFIG.powerup.doublePointsDuration;
      break;
    case 'magnet':
      _active.magnet = CONFIG.powerup.magnetDuration;
      break;
  }
}

/**
 * Per-frame update of all active power-up effects.
 * Decrements timers, applies freeze velocity scaling, and applies magnet pull.
 *
 * Freeze effect: sets vx/vy on every fruit to freezeVelocityScale * current vx/vy
 *   ONLY for the first frame of activation (velocity is already scaled going forward
 *   by physics). This implementation clamps fruit speed each frame when freeze is active.
 *
 * Magnet effect: pulls each fruit toward pointerPos if within magnetRadius.
 *
 * @param dt         - Frame delta in seconds (unscaled — power-up timers run on wall time).
 * @param fruits     - All live fruit entities (may be modified in-place for magnet pull).
 * @param pointerPos - Current pointer position for magnet pull; null = no hand.
 */
export function tickActivePowerUps(
  dt: number,
  fruits: readonly Fruit[],
  pointerPos: Vec2 | null,
): void {
  const dtMs = dt * 1000;

  // Freeze timer.
  if (_active.freeze !== null) {
    _active.freeze -= dtMs;
    if (_active.freeze <= 0) {
      _active.freeze = null;
    } else {
      // Scale velocity toward freeze speed each frame.
      const scale = CONFIG.powerup.freezeVelocityScale;
      for (const f of fruits) {
        f.vx *= scale + (1 - scale) * 0; // snap to scaled — repeated application converges
        f.vy *= scale + (1 - scale) * 0;
      }
    }
  }

  // Double-points timer (no per-frame entity effect — ScoreManager queries isFreezeActive()).
  if (_active.double !== null) {
    _active.double -= dtMs;
    if (_active.double <= 0) {
      _active.double = null;
    }
  }

  // Magnet timer.
  if (_active.magnet !== null) {
    _active.magnet -= dtMs;
    if (_active.magnet <= 0) {
      _active.magnet = null;
    } else if (pointerPos !== null) {
      // Pull each fruit within magnetRadius toward the pointer.
      const pullSpeed = CONFIG.powerup.magnetPullSpeed;
      const magnetR = CONFIG.powerup.magnetRadius;

      for (const f of fruits) {
        const dx = pointerPos.x - f.x;
        const dy = pointerPos.y - f.y;
        const distSq = dx * dx + dy * dy;

        if (distSq < magnetR * magnetR && distSq > 0) {
          const dist = Math.sqrt(distSq);
          const nx = dx / dist;
          const ny = dy / dist;

          // Apply pull toward pointer (does not override gravity entirely).
          f.vx += nx * pullSpeed * dt;
          f.vy += ny * pullSpeed * dt;
        }
      }
    }
  }
}

/**
 * Returns true if the Double Points effect is currently active.
 * ScoreManager calls this to apply the ×2 multiplier.
 */
export function isDoublePointsActive(): boolean {
  return _active.double !== null && _active.double > 0;
}

/**
 * Returns true if the Freeze effect is currently active.
 */
export function isFreezeActive(): boolean {
  return _active.freeze !== null && _active.freeze > 0;
}

/**
 * Returns true if the Magnet effect is currently active.
 */
export function isMagnetActive(): boolean {
  return _active.magnet !== null && _active.magnet > 0;
}

/**
 * Returns remaining duration (ms) for each active effect (0 if inactive).
 * Used by HUD power-up indicators.
 */
export function getActiveDurations(): { freeze: number; double: number; magnet: number } {
  return {
    freeze: _active.freeze ?? 0,
    double: _active.double ?? 0,
    magnet: _active.magnet ?? 0,
  };
}

/**
 * Reset all active power-up effects (call on game restart or state transition).
 */
export function resetPowerUps(): void {
  _active.freeze = null;
  _active.double = null;
  _active.magnet = null;
}

/**
 * Remove power-up sprite from scene and free resources.
 *
 * @param powerUp   - The power-up entity to destroy.
 * @param container - PixiJS Container to remove sprites from.
 */
export function destroyPowerUp(powerUp: PowerUp, container: ContainerRef): void {
  const pu = powerUp as PowerUpInternal;

  if (pu._sprite !== null) {
    container.removeChild(pu._sprite);
    pu._sprite.destroy?.();
    pu._sprite = null;
  }
}
